import { AUTH_STATE } from '@marriott/mi-headless-utils';
import { STATUS_IN_BOOLEAN } from './constants';
import { accountAgeCalculator, sanitizeObject, ternaryHandler, updateDateFormat } from './helper';
type STATUS_TYPE = keyof typeof STATUS_IN_BOOLEAN;

/**
 * Generates ACDL (Adobe Client Data Layer) keys for user sign-up events.
 *
 * @function signInACDLkeyGenerator
 * @param {STATUS_TYPE} status - The current status of sign-up process
 * @param {string} enrollmentSource - The source system or method used for enrollment
 * @returns {Object} - Returns an object containing user data with cleaned payload
 *
 * @example
 * //Generate ACDL keys for successful sign-up
 * const result = signInACDLKeyGenerator(STATUS_IN_BOOLEAN.TRUE, "MSAN");
 * console.log(result)
 */
export const signInACDLkeyGenerator = (status: STATUS_TYPE, enrollmentSource: string) => {
  const payload = {
    enrollment_source: enrollmentSource,
    enrolled_status: status,
  };
  const cleanedPayload = sanitizeObject(payload);
  return {
    USER: cleanedPayload,
  };
};

/**
 * Generates ACDL (Adobe Client Data Layer) keys for user login events.
 *
 * @function loginACDLkeyGenerator
 * @param {string} loginEventDetailsFromHeader - This will be taken from loginEventDetails header from response
 * @param {STATUS_TYPE} status - The current status of login process
 * @param {dataLayerType} datalayer - The current dataLayer avaliable
 * @returns {Object} - Returns an object containing user data with cleaned payload
 *
 * @example
 * //Generate ACDL keys for successful login
 * const result = loginACDLkeyGenerator(loginEventDetails,STATUS_IN_BOOLEAN.TRUE, dataLayer);
 * console.log(result)
 */
export const loginACDLkeyGenerator = (
  loginEventDetailsFromHeader: string,
  status: STATUS_TYPE,
  datalayer?: dataLayerType
) => {
  const [loginEventDetails, loginApiResponse, _, loginResponseId] = loginEventDetailsFromHeader.split('|');
  // need to get all the static values from account team
  const payload = {
    authentication_status: ternaryHandler(datalayer?.['memState']),
    login_event_details: loginEventDetails,
    login_api_response: loginApiResponse,
    login_response_id: loginResponseId,
  };
  if (payload.authentication_status === AUTH_STATE.remembered && status === STATUS_IN_BOOLEAN.FALSE) {
    Object.assign(payload, {
      alt_customer_id: ternaryHandler(datalayer?.['altCustId'], ternaryHandler(datalayer?.['mr_id_alternate'])),
    });
  }
  if (status === STATUS_IN_BOOLEAN.TRUE) {
    Object.assign(payload, {
      authentication_status: 'authenticated', //assuming success will always authenticated
    });
  }
  const cleanedPayload = sanitizeObject(payload);
  return {
    USER: cleanedPayload,
  };
};

export const enrollACDLkeyGenerator = (enrollEventDetailsFromHeader: string) => {
  // Split the header into up to 4 parts
  const [enrollEventDetails, enrollApiResponse, enrollError, enrollResponseId] =
    enrollEventDetailsFromHeader.split('|');
  // Build the payload with all available fields
  const payload: Record<string, string | undefined> = {
    enroll_event_details: enrollEventDetails,
    enroll_api_response: enrollApiResponse,
    enroll_error: enrollError,
    enroll_response_id: enrollResponseId,
  };
  const cleanedPayload = sanitizeObject(payload);
  return {
    USER: cleanedPayload,
  };
};

/**
 *
 * Generates ACDL (Adobe Client Data Layer) keys for PAGEVIEW events.
 * @param {Object} args
 * @param {dataLayerType} args.datalayer - The current dataLayer avaliable
 * @param {boolean} args.isModel - Flag indicating if it's model based PAGEVIEW
 * @param {string} args.modelName - name of the model if it's model based tracking
 * @returns {Object} - Returns an object containing metadata, page & user data
 */
export const pageViewACDLkeyGenerator = ({ datalayer, isModel, modelName }: pageViewACDLkeyGeneratorType) => {
  const payload = {
    metadata: {
      allow_marketing: true, //always true if ACDL enabled
      site_id: ternaryHandler(datalayer?.['env_site_id']),
      language_domain: ternaryHandler(datalayer?.['env_site_id']),
      platform: ternaryHandler(datalayer?.['env_platform']),
      server_id: ternaryHandler(datalayer?.['env_server_id']),
      session_id: ternaryHandler(datalayer?.['sessionId']),
      latitude: ternaryHandler(datalayer?.['browser_akamai_loc_lat']),
      longitude: ternaryHandler(datalayer?.['browser_akamai_loc_long']),
      geo_country: ternaryHandler(datalayer?.['browser_akamai_loc_country']),
      geo_state: ternaryHandler(datalayer?.['browser_akamai_loc_state']),
      geo_city: ternaryHandler(datalayer?.['browser_akamai_loc_city']),
    },
    page: {
      name: isModel ? modelName : window.location.host + window.location.pathname + window.location.hash, // model name need to confirm
      modal: !!isModel,
    },
    // key value pairs of data about the user
    user: {
      alt_customer_id: ternaryHandler(datalayer?.['altCustId'], ternaryHandler(datalayer?.['mr_id_alternate'])),
      authentication_status: ternaryHandler(datalayer?.['memState']),
      member_level: ternaryHandler(datalayer?.['mr_prof_member_program_level']),
      member_city: ternaryHandler(datalayer?.['mr_prof_address_city']),
      member_state: ternaryHandler(datalayer?.['mr_prof_address_state']),
      member_country: ternaryHandler(datalayer?.['mr_prof_address_country']),
      upcoming_stay: ternaryHandler(datalayer?.['mr_prof_upcoming_stay']),
      stay_in_progress: ternaryHandler(datalayer?.['mr_prof_stay_in_progress']),
      cc_all: ternaryHandler(datalayer?.['mr_prof_cc_all']),
      nights_booked_this_year: ternaryHandler(datalayer?.['mr_prof_nights_booked_this_year']),
      points_balance: ternaryHandler(datalayer?.['mr_prof_points_balance']),
      member_join_date: updateDateFormat(ternaryHandler(datalayer?.['mr_prof_join_date']) as string),
      account_age: accountAgeCalculator(datalayer ?? {}),
    },
  };
  const cleanedPayload = sanitizeObject(payload);
  return cleanedPayload;
};

/**
 *
 * @param jsonObj refers to pageModelJSON
 * @param compType AEM mapping url of the component
 * @returns updated model json
 * @description it will iterate over the model json and will look for that compType,
 * if it exists then it will inject property to that component model json
 *
 */
export const updateModelJSON = (jsonObj: Record<string, unknown>, compType: string[] = []) => {
  function traverse(obj: Record<string, unknown>) {
    if (obj !== null && typeof obj === 'object') {
      if (compType.includes(String(obj[':type']))) {
        obj['isHide'] = true;
        obj['mboxParameter'] = undefined;
        return true;
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          traverse(obj[key] as Record<string, unknown>);
        }
      }
    }
    return false;
  }

  traverse(jsonObj);
  return jsonObj;
};

/**
 *
 * @param model refers to the page model json
 * @returns boolean
 *
 * @description this method is useful to identify whether ACDL is enabled or not
 * based on pageName + locale.
 *
 * NOTE: PLEASE RE-USE THIS METHOD ON SERVER SIDE ONLY, OTHERWISE ENV VALUES CANT BE FETCHED PROPERLY..
 */
/**
 * Normalizes locale strings into the canonical format used for matching.
 *
 * Examples:
 * - "en_US" -> "en-us"
 * - " EN-us " -> "en-us"
 *
 * We normalize because locale sources are inconsistent (AEM/model often uses "_" while config uses "-"),
 * and we want comparisons to be case-insensitive.
 */
const normalizeLocale = (value: string) => value?.trim()?.toLowerCase()?.replace('_', '-');

/**
 * Normalizes page names for stable comparisons and lookup.
 *
 * We normalize to avoid mismatches caused by casing/whitespace differences between code and flag config.
 */
const normalizePageName = (value: string) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/**
 * Returns true if the current locale matches an allowed locale entry.
 *
 * Matching rules:
 * - If allowed locale is region-specific (contains "-"), it must match exactly (e.g. "en-us").
 * - If allowed locale is language-only (no "-"), it matches any region for that language
 *   (e.g. "es" matches "es", "es-es", "es-mx", etc).
 *
 * This gives config authors a compact way to enable a language broadly without listing every region.
 */
const localeMatches = (allowedLocale: string, currentLocale: string) => {
  const allowed = normalizeLocale(allowedLocale);
  const current = normalizeLocale(currentLocale);
  if (!allowed || !current) return false;

  // Support both "en-us" style and language-only "es" style entries.
  return allowed.includes('-') ? current === allowed : current === allowed || current.startsWith(`${allowed}-`);
};

/**
 * Parses the ACDL enabled pages+locales feature flag value as a page->locales mapping.
 *
 * Expected input format:
 * - Preferred: JSON object (from `FLAG_TYPE.JSON`):
 *   `{ "sign-in": ["en-us", "es"], "profile": ["en-us"] }`
 *   or `{ "sign-in": "en-us,es", "profile": "en-us" }`
 * - Back-compat: JSON string containing one of the above objects.
 *
 * Output:
 * - A map of normalized pageName -> array of normalized locales.
 *
 * Notes:
 * - We intentionally require valid JSON (no "JS object literal" parsing) for safety and predictability.
 * - Values may be a comma-separated string or an array of strings.
 */
const parsePageLocaleMap = (raw: unknown): Record<string, string[]> | null => {
  if (raw == null) return null;

  let obj: unknown = raw;

  // Back-compat: some integrations may still provide a JSON string.
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      obj = JSON.parse(text);
    } catch {
      return null;
    }
  }

  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;

  const result: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const normalizedKey = normalizePageName(key);
    if (!normalizedKey) continue;

    let locales: string[] = [];
    if (typeof value === 'string') {
      locales = value.split(',');
    } else if (Array.isArray(value)) {
      locales = value.filter((v): v is string => typeof v === 'string');
    } else {
      continue;
    }

    const normalizedLocales = locales.map(normalizeLocale).filter(Boolean) as string[];
    if (normalizedLocales.length > 0) result[normalizedKey] = normalizedLocales;
  }

  return Object.keys(result).length ? result : null;
};

/**
 * Determines whether ACDL/WebSDK should be enabled for the current request.
 *
 * Enablement requires:
 * - WebSDK globally enabled via env: `NEXT_PUBLIC_ENABLE_WEBSDK === "true"`
 * - WebSDK enabled in the page model: `model["enableWebSDK"] === "true"`
 * - AND the current locale is enabled for the given `pageName`
 *
 * Locale source:
 * - Uses `model["language"]` (e.g. "en_US") and normalizes it to "en-us".
 *
 * Config source (priority order):
 * - `locales` argument (typically from feature flags)
 * - fallback to `process.env["NEXT_PUBLIC_ACDL_ENABLE_LOCALES"]`
 *
 * Supported config formats:
 * - Preferred (page-specific): JSON map `{ "page": ["en-us","es"] }` (or `{ "page": "en-us,es" }`)
 * - Back-compat (page-specific): JSON *string* map `{ "page": "en-us,es" }`
 * - Back-compat (all pages): comma-separated locale list `"en-us,es"`
 */
export const isACDLEnabled = (model: Record<string, unknown>, locales: unknown, pageName: string) => {
  const currentLocale = normalizeLocale(String(model?.['language'] ?? ''));
  const envFallback = process.env['NEXT_PUBLIC_ACDL_ENABLE_LOCALES'] ?? '';

  // Harness returns `{}` for missing JSON flags; treat that as "unset" so env fallback can still work.
  const hasUsableConfig = (value: unknown) => {
    if (value == null || value == undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
    return true;
  };

  const rawConfig: unknown = hasUsableConfig(locales) ? locales : envFallback;

  const isWebSdkEnabled =
    String(process.env['NEXT_PUBLIC_ENABLE_WEBSDK']) === 'true' && String(model?.['enableWebSDK']) === 'true';
  if (!isWebSdkEnabled || !currentLocale) return false;

  const pageLocaleMap = parsePageLocaleMap(rawConfig);
  if (pageLocaleMap) {
    const allowedLocalesForPage = pageLocaleMap[normalizePageName(pageName)] ?? [];
    return allowedLocalesForPage.some(allowed => localeMatches(allowed, currentLocale));
  }

  // Back-compat: allow arrays (interpreted as "all pages") and plain comma-separated locale lists.
  const allowedLocales = Array.isArray(rawConfig)
    ? (rawConfig
        .filter((v): v is string => typeof v === 'string')
        .map(normalizeLocale)
        .filter(Boolean) as string[])
    : (String(rawConfig ?? '')
        .toLowerCase()
        .split(',')
        .map(normalizeLocale)
        .filter(Boolean) as string[]);

  return allowedLocales?.some(allowed => localeMatches(allowed, currentLocale));
};
